<script>
    import systemStatusService from '@/services/systemStatus.service';
    import StatusUptimeBars from '@/components/systemStatus/StatusUptimeBars';
    import Spinner from '@/components/shared/Spinner';
    import { SERVICE_STATE } from '@/enums/ServiceState';

    const POLL_INTERVAL_MS = 30000;

    export default {
        components: {
            StatusUptimeBars,
            Spinner
        },
        data() {
            return {
                status: null,
                isLoading: false,
                loadError: null,
                expanded: {},
                secondsSinceCheck: 0,
                pollTimer: null,
                tickTimer: null,
                controller: null
            };
        },
        computed: {
            groups() {
                const groups = [];
                const byName = new Map();

                (this.status?.services ?? []).forEach(service => {
                    if (!byName.has(service.group)) {
                        byName.set(service.group, []);
                        groups.push(service.group);
                    }

                    byName.get(service.group).push(service);
                });

                return groups.map(name => ({ name, services: byName.get(name) }));
            },
            bannerClass() {
                return this.stateClass(this.status?.overallState);
            },
            checkedLabel() {
                if (!this.status) return '';
                if (this.secondsSinceCheck < 5) return 'Checked just now';
                if (this.secondsSinceCheck < 60) return `Checked ${this.secondsSinceCheck}s ago`;

                return `Checked ${Math.floor(this.secondsSinceCheck / 60)}m ago`;
            }
        },
        methods: {
            async loadData(refresh = false) {
                const controller = new AbortController();
                if (this.controller) this.controller.abort();
                this.controller = controller;
                this.isLoading = true;

                try {
                    this.status = await systemStatusService.get(refresh, controller.signal);
                    this.loadError = null;
                    this.secondsSinceCheck = 0;
                } catch (err) {
                    if (err?.name === 'AbortError') return;
                    this.loadError = err?.data?.message || err?.message || 'Could not load system status.';
                } finally {
                    if (this.controller === controller) this.isLoading = false;
                }
            },
            stateClass(state) {
                if (state === SERVICE_STATE.DOWN) return 'is-down';
                if (state === SERVICE_STATE.DEGRADED) return 'is-degraded';

                return 'is-operational';
            },
            stateIcon(isOk) {
                return isOk ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger';
            },
            isExpanded(service) {
                return !!this.expanded[service.name];
            },
            toggle(service) {
                this.expanded = { ...this.expanded, [service.name]: !this.expanded[service.name] };
            },
            serviceUpDetail(service) {
                if (!service.isServiceUp) return service.error || 'No response';

                return `HTTP ${service.httpStatusCode} in ${service.latencyMs} ms`;
            },
            databaseDetail(service) {
                if (service.sqlError) return service.sqlError;
                if (!service.isDatabaseHealthy) return service.error || 'Not verified';
                if (service.isDatabaseInferred)
                    return `Inferred from HTTP ${service.httpStatusCode} — health-check v${service.healthCheckVersion ?? '?'} does not report its own SQL probe`;

                return 'SELECT 1 succeeded';
            },
            catalogDetail(service) {
                if (service.isCatalogCorrect) return service.catalog || 'Not reported';

                return `MISMATCH — expected ${this.status?.expectedCatalog}, got ${service.catalog || 'nothing'}`;
            }
        },
        mounted() {
            this.loadData();

            // Polls without forcing a refresh so every open page shares the server-side cache.
            this.pollTimer = setInterval(() => this.loadData(), POLL_INTERVAL_MS);
            this.tickTimer = setInterval(() => this.secondsSinceCheck++, 1000);
        },
        beforeUnmount() {
            clearInterval(this.pollTimer);
            clearInterval(this.tickTimer);
            if (this.controller) this.controller.abort();
        }
    };
</script>

<template>
    <div class="main-content-header">
        <h3>
            <i class="bi-activity"></i>
            System Status
        </h3>
        <div class="ms-auto d-flex align-items-center gap-3">
            <span class="text-muted small">{{ checkedLabel }}</span>
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="isLoading" @click="loadData(true)">
                <i class="bi-arrow-clockwise me-1"></i>
                Refresh
            </button>
        </div>
    </div>
    <div class="main-content-body system-status">
        <div v-if="loadError" class="alert alert-danger">
            <i class="bi-exclamation-triangle-fill me-2"></i>
            {{ loadError }}
        </div>

        <div v-if="status?.warning" class="alert alert-warning">
            <i class="bi-exclamation-triangle-fill me-2"></i>
            {{ status.warning }}
        </div>

        <div v-if="!status" class="system-status-loading">
            <Spinner :show="isLoading" text="Checking services..." />
        </div>

        <template v-if="status">
            <div class="status-banner" :class="bannerClass">{{ status.overallMessage }}</div>

            <div class="d-flex align-items-baseline justify-content-between text-muted small mb-2">
                <span>
                    Checking <strong>{{ status.environment }}</strong> services against
                    <strong>{{ status.expectedCatalog }}</strong>
                </span>
                <span>Uptime over the past 90 days.</span>
            </div>

            <div v-for="group in groups" :key="`status-group-${group.name}`" class="status-card">
                <div class="status-card-title">{{ group.name }}</div>
                <div v-for="service in group.services" :key="`status-service-${service.name}`" class="status-row">
                    <div class="status-row-head">
                        <button type="button" class="status-row-toggle" @click="toggle(service)">
                            <i :class="isExpanded(service) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                            <span class="status-row-name">{{ service.name }}</span>
                        </button>
                        <span class="status-row-state" :class="stateClass(service.state)">{{ service.stateLabel }}</span>
                    </div>

                    <StatusUptimeBars :state="service.state" />

                    <div v-if="isExpanded(service)" class="status-row-detail bg-light border rounded-1 p-3 mt-3">
                        <div class="status-check">
                            <i :class="stateIcon(service.isServiceUp)"></i>
                            <span class="status-check-name">{{
                                service.hasDatabase === false ? 'SignalR negotiate' : 'Service responding'
                            }}</span>
                            <span class="status-check-value" :class="{ 'text-danger': !service.isServiceUp }">{{
                                serviceUpDetail(service)
                            }}</span>
                        </div>
                        <div v-if="service.hasDatabase !== false" class="status-check">
                            <i :class="stateIcon(service.isDatabaseHealthy)"></i>
                            <span class="status-check-name">Database connection</span>
                            <span class="status-check-value" :class="{ 'text-danger': !service.isDatabaseHealthy }">{{
                                databaseDetail(service)
                            }}</span>
                        </div>
                        <div v-if="service.hasDatabase !== false" class="status-check">
                            <i :class="stateIcon(service.isCatalogCorrect)"></i>
                            <span class="status-check-name">Target database</span>
                            <span class="status-check-value" :class="{ 'text-danger': !service.isCatalogCorrect }">{{
                                catalogDetail(service)
                            }}</span>
                        </div>
                        <div class="status-row-meta">
                            <span>{{ service.url }}</span>
                            <span v-if="service.environment">Environment: {{ service.environment }}</span>
                            <span v-if="service.healthCheckVersion">health-check v{{ service.healthCheckVersion }}</span>
                            <span v-if="service.reportedStatus">Reported: {{ service.reportedStatus }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
    @import '@/styles/_status-colors.scss';

    .system-status {
        max-width: 1100px;

        .system-status-loading {
            position: relative;
            min-height: 160px;
        }

        .status-banner {
            padding: 1.5rem;
            border-radius: 4px;
            margin-bottom: 3rem;
            color: white;
            font-size: 1.5rem;
            font-weight: 700;

            &.is-operational {
                background-color: $status-operational;
            }

            &.is-degraded {
                background-color: $status-degraded;
            }

            &.is-down {
                background-color: $status-down;
            }
        }

        .status-card {
            border: 1px solid #e3e3e3;
            border-radius: 4px;
            background-color: white;
            margin-bottom: 1.5rem;

            .status-card-title {
                padding: 0.625rem 1.25rem;
                border-bottom: 1px solid #e3e3e3;
                background-color: #fafafa;
                font-size: 0.8125rem;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.04em;
                color: #8c8c8c;
            }

            .status-row {
                padding: 1.25rem;

                & + .status-row {
                    border-top: 1px solid #eee;
                }
            }
        }

        .status-row-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 0.75rem;
        }

        .status-row-toggle {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0;
            border: 0;
            background: none;
            color: #333;

            i {
                font-size: 0.75rem;
                color: #adadad;
            }

            .status-row-name {
                font-size: 1.0625rem;
                font-weight: 700;
            }

            &:hover .status-row-name {
                text-decoration: underline;
            }
        }

        .status-row-state {
            font-size: 0.9375rem;

            &.is-operational {
                color: $status-operational;
            }

            &.is-degraded {
                color: $status-degraded-text;
            }

            &.is-down {
                color: $status-down;
            }
        }

        .status-check {
            display: flex;
            align-items: baseline;
            gap: 0.5rem;
            font-size: 0.875rem;

            & + .status-check {
                margin-top: 0.375rem;
            }

            .status-check-name {
                min-width: 170px;
                font-weight: 600;
            }

            .status-check-value {
                color: #555;
                word-break: break-word;
            }
        }

        .status-row-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 0 1.25rem;
            margin-top: 0.75rem;
            padding-top: 0.75rem;
            border-top: 1px solid #e3e3e3;
            font-size: 0.8125rem;
            color: #8c8c8c;
        }
    }
</style>
